import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import frenchi18n from '@assets/i18n/fr.json';
import { environment } from '@environments/environment';
import { Bank } from '@modules/process/models/bank';
import { ProcessEventFactory } from '@shared/models/processEvents';
import { TrackerService } from '@shared/services/tracker.service';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { Account } from '@core/model/domain/account';
import { assert } from 'ts-essentials';
import { SelectedAccountDto } from '@core/service/digital-factory/account/types/accountDto';
import { AccountsService } from './services/accounts.service';

@Component({
  selector: 'app-select-accounts',
  templateUrl: './select-accounts.component.html',
  styleUrls: ['./select-accounts.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: { class: 'max-w-full d-flex flex-col justify-center items-center' },
})
export class SelectAccountsComponent implements OnInit {
  isPopinOpen = false;
  isTestBankConnector = false;
  accountsSelectionForm!: UntypedFormGroup;
  moreThanOneAccount = false;
  selectingAccounts = false;
  countSelectedBank!: number;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private processEventService: ProcessEventService,
    private storeService: StoreService,
    private accountsService: AccountsService,
    private tracker: TrackerService
  ) {
    this.foundAccounts.forEach((item) => {
      item['checked'] = false;
    });
  }

  ngOnInit(): void {
    this.tracker.trackPage({ taggingPage: 'SELECT_ACCOUNTS', taggingPayload: this.foundAccounts.length });

    this.isTestBankConnector =
      this.selectedBank.uid === environment.testBankConnector && environment.env !== 'prod';
    this.moreThanOneAccount = this.foundAccounts.length > 1;

    this.accountsSelectionForm = this.formBuilder.group(this.buildAccountsFormControls(this.foundAccounts));
    this.accountsSelectionForm.setValidators(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.accountsSelectionValidator.bind(this)
    );
  }

  changed() {
    this.countSelectedBank = 0;
    this.foundAccounts.forEach((item) => {
      if (item['checked']) {
        this.countSelectedBank = this.countSelectedBank + 1;
      }
    });
  }

  openPopin() {
    this.isPopinOpen = true;
  }

  closePopin() {
    this.isPopinOpen = false;
  }

  accountsSelectionValidator(formGroup: UntypedFormGroup): { atLeastOneSelected: boolean } | null {
    const valid = this.foundAccounts.find((account) => formGroup.controls[account.id]?.value);
    return valid
      ? null
      : {
          atLeastOneSelected: true,
        };
  }

  buildAccountsFormControls(accounts: Account[]): { [key: string]: boolean | string } {
    const accountsFormControls: { [key: string]: boolean | string } = {};
    accounts.forEach((account) => (accountsFormControls[account.id] = false));

    if (this.isTestBankConnector) {
      accounts.forEach((account) => {
        if (account.iban) {
          accountsFormControls[account.iban] = account.iban;
        }
      });
    }

    return accountsFormControls;
  }

  submit() {
    this.tracker.trackClick({
      taggingPage: 'SELECT_ACCOUNTS',
      click: frenchi18n.common_continue,
      taggingPayload: this.foundAccounts.length,
    });

    const formValue = this.accountsSelectionForm.value as {
      [key: string]: boolean;
    };

    const request: SelectedAccountDto[] = this.foundAccounts
      .filter((account) => formValue[account.id])
      .map((account) => {
        return {
          id: account.id,
        };
      });
    assert(request != null, 'We must have selected accounts.');

    this.selectingAccounts = true;

    this.accountsService
      .selectAccountsThenRequestAnalysisThenWaitForCaseState({
        userId: this.storeService.getVariableOrThrow('USER_ID'),
        userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
        connectionId: this.storeService.getVariableOrThrow('SELECTED_CONNECTION_ID'),
        selectAccountsRequest: { accounts: request },
      })
      .subscribe({
        next: (res) => {
          this.selectingAccounts = false;
          const filteredFoundAccounts = this.foundAccounts.filter((account) => formValue[account.id]);

          if (res.success) {
            this.processEventService.push(
              ProcessEventFactory.SpawnSelectedAccountsEvent(res.value, filteredFoundAccounts, 'result_page')
            );
          } else {
            this.processEventService.push(
              ProcessEventFactory.SpawnSelectedAccountsEvent('RETRY_BANK', [], 'result_page')
            );
          }
        },
        error: () => {
          this.processEventService.push(
            ProcessEventFactory.SpawnSelectedAccountsEvent('RETRY_BANK', [], 'result_page')
          );
        },
      });
  }

  setIBAN(value: Event, i: number) {
    const tempAccounts = this.storeService.getVariableOrThrow('FOUND_ACCOUNTS');
    const selectedAccount = tempAccounts[i];
    assert(selectedAccount != null, 'we must have a selected account.');
    const targetValue = value.target && value.target['value'];
    selectedAccount.iban = targetValue;
    this.storeService.setVariable('FOUND_ACCOUNTS', tempAccounts);
  }

  abandon() {
    this.tracker.trackClick({
      taggingPage: 'SELECT_ACCOUNTS',
      click: frenchi18n.select_accounts_abandon,
      taggingPayload: this.foundAccounts.length,
    });

    this.processEventService.push(ProcessEventFactory.SpawnAbandonEvent());
  }

  get selectedBank(): Bank {
    return this.storeService.getVariableOrThrow('SELECTED_BANK');
  }

  set selectedBank(value: Bank) {
    this.storeService.setVariable('SELECTED_BANK', value);
  }

  get foundAccounts(): Account[] {
    return this.storeService.getVariableOrThrow('FOUND_ACCOUNTS');
  }

  set foundAccounts(value: Account[]) {
    this.storeService.setVariable('FOUND_ACCOUNTS', value);
  }
}
